import { NgTemplateOutlet } from '@angular/common';
import type { OnInit, SimpleChanges, TemplateRef } from '@angular/core';
import { ChangeDetectorRef, Component, Input, output, inject, input } from '@angular/core';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { collapse, CollapseState, trackByProp } from '@core/common-legacy';
import { IconComponent, IconName } from '@core/user-interface-legacy';

import type { AnimState, MenuItem } from '../interfaces';
import { SidebarInternalService } from '../sidebar/sidebar-internal.service';
import { ItemType, SidebarItemComponent } from '../sidebar-item/sidebar-item.component';

@Component({
  selector: 'rt-sidebar-group',
  templateUrl: './sidebar-group.component.html',
  styleUrls: ['./sidebar-group.component.less'],
  animations: [collapse],
  imports: [IconComponent, SidebarItemComponent, NgTemplateOutlet],
})
export class SidebarGroupComponent implements OnInit {
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly sidebarInternalService = inject(SidebarInternalService);

  public readonly heading = input<string>(undefined!);
  public readonly items = input<MenuItem[]>([]);
  public readonly initialExpanded = input<boolean>(true);
  public readonly ItemType = input<typeof ItemType>(ItemType);
  public readonly isFirst = input<boolean>(false);
  public readonly isLast = input<boolean>(false);
  public readonly toggleWithSidebar = input<boolean>(false);
  public readonly icon = input<IconName>(undefined!);
  public readonly walkthroughStepTemplate = input<TemplateRef<any>>(undefined!);
  @Input() public rightSlot: TemplateRef<{ state: CollapseState }>;

  public state = output<AnimState>();

  public isSidebarMinimized: boolean = false;
  public isGroupMinimized: boolean;
  public trackByName = trackByProp<MenuItem>({ prop: 'name' });

  public get groupVisibilityState(): CollapseState {
    if (this.toggleWithSidebar()) {
      return this.isGroupMinimized || this.isSidebarMinimized
        ? CollapseState.Collapsed
        : CollapseState.Normal;
    }

    return this.isGroupMinimized ? CollapseState.Collapsed : CollapseState.Normal;
  }

  private ngOnDestroy$ = new Subject<void>();

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes?.initialExpanded?.currentValue) {
      this.isGroupMinimized = !this.initialExpanded();
    }
  }

  public ngOnInit(): void {
    this.sidebarInternalService.sidebarMinimized$
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(this.onSidebarMinimize.bind(this));
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public onHeaderClick(): void {
    if (this.toggleWithSidebar() && this.isSidebarMinimized) {
      return;
    }

    this.toggleMinimized();
    this.state.emit({
      animState: this.isGroupMinimized ? CollapseState.Collapsed : CollapseState.Normal,
    });

    this.cdRef.detectChanges();
  }

  public toggleMinimized(value?: boolean): void {
    this.isGroupMinimized = value != null ? value : !this.isGroupMinimized;
  }

  private onSidebarMinimize(isMinimized: boolean): void {
    this.isSidebarMinimized = isMinimized;
    this.cdRef.detectChanges();
  }
}
